import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';

const CHURCH_TYPES = new Set(['church', 'cathedral', 'chapel', 'basilica', 'abbey', 'monastery']);

/**
 * Build small 3D amenity features:
 *   – Fountains (node features: amenity=fountain)
 *   – Church crosses (derived from already-parsed building data)
 *
 * @param {Array}    fountains  Parsed fountain nodes: { id, lat, lon }
 * @param {Array}    buildings  Full buildings array (filtered for church types)
 * @param {Object}   proj       Projection helper
 * @param {Function} elevFn     Optional: elevFn(x, z) → metres above scene centre
 * @returns {THREE.Group}
 */
export function buildAmenityMesh(fountains, buildings, proj, elevFn = null) {
  const group = new THREE.Group();

  _addFountains(group, fountains, proj, elevFn);
  _addChurchCrosses(group, buildings, proj, elevFn);

  return group;
}

// ── Fountains ─────────────────────────────────────────────────────────────────

function _addFountains(group, fountains, proj, elevFn) {
  if (fountains.length === 0) return;

  const basinGeos = [];
  const jetGeos   = [];

  for (const f of fountains) {
    const p = proj.project(f.lat, f.lon);
    if (!isFinite(p.x) || !isFinite(p.z)) continue;

    const y = elevFn ? elevFn(p.x, p.z) : 0;

    // Basin: flat cylinder at ground level
    const basin = new THREE.CylinderGeometry(2.0, 2.2, 0.35, 16);
    basin.translate(p.x, y + 0.17, p.z);
    basinGeos.push(basin);

    // Water jet: narrow upward-pointing cone above basin centre
    const jet = new THREE.ConeGeometry(0.15, 1.4, 6);
    jet.translate(p.x, y + 0.35 + 0.7, p.z);
    jetGeos.push(jet);
  }

  if (basinGeos.length > 0) {
    const merged = mergeGeometries(basinGeos, false);
    basinGeos.forEach(g => g.dispose());
    const mat  = new THREE.MeshLambertMaterial({ color: 0x8899aa }); // stone/marble
    const mesh = new THREE.Mesh(merged, mat);
    mesh.castShadow = mesh.receiveShadow = true;
    group.add(mesh);
  }

  if (jetGeos.length > 0) {
    const merged = mergeGeometries(jetGeos, false);
    jetGeos.forEach(g => g.dispose());
    const mat  = new THREE.MeshLambertMaterial({
      color: 0x88ccee, transparent: true, opacity: 0.75,
    });
    group.add(new THREE.Mesh(merged, mat));
  }
}

// ── Church crosses ────────────────────────────────────────────────────────────

function _addChurchCrosses(group, buildings, proj, elevFn) {
  const vGeos = []; // vertical bars
  const hGeos = []; // horizontal bars

  for (const bldg of buildings) {
    if (!CHURCH_TYPES.has((bldg.buildingType || '').toLowerCase())) continue;

    // Compute centroid of outer ring
    let sx = 0, sz = 0, cnt = 0;
    for (const n of bldg.nodes) {
      if (n.lat === undefined) continue;
      const p = proj.project(n.lat, n.lon);
      if (!isFinite(p.x)) continue;
      sx += p.x; sz += p.z; cnt++;
    }
    if (cnt === 0) continue;

    const cx = sx / cnt;
    const cz = sz / cnt;
    const groundY = elevFn ? elevFn(cx, cz) : 0;
    const roofY   = groundY + (bldg.height || 10);

    // Vertical bar
    const vGeo = new THREE.BoxGeometry(0.2, 2.2, 0.2);
    vGeo.translate(cx, roofY + 1.1 + 0.3, cz); // base 0.3m above roof
    vGeos.push(vGeo);

    // Horizontal bar (1/3 from top of vertical bar)
    const hGeo = new THREE.BoxGeometry(1.1, 0.2, 0.2);
    hGeo.translate(cx, roofY + 0.3 + 2.2 * 0.65, cz);
    hGeos.push(hGeo);
  }

  const crossMat = new THREE.MeshLambertMaterial({ color: 0xd4c090 }); // warm stone

  if (vGeos.length > 0) {
    const merged = mergeGeometries(vGeos, false);
    vGeos.forEach(g => g.dispose());
    group.add(new THREE.Mesh(merged, crossMat));
  }
  if (hGeos.length > 0) {
    const merged = mergeGeometries(hGeos, false);
    hGeos.forEach(g => g.dispose());
    // Share the same material instance
    group.add(new THREE.Mesh(merged, crossMat));
  }
}
